/* @flow */
import { isEmpty } from 'lodash';
import * as React from 'react';

import Button from 'components/Button.jsx';
import Buttons from 'components/Buttons.jsx';
import Glyph from 'components/Glyph.jsx';
import Icon from 'components/Icon.jsx';
import PeopleListProfile from 'components/PeopleList/PeopleListProfile';
import Tooltip from 'components/Tooltip.jsx';
import TooltipTrigger from 'components/TooltipTrigger.jsx';
import User from 'entities/User';
import Subscriber from 'entities/Subscriber';
import PhoneCallButton from 'phoneCalls/components/PhoneCallButton.jsx';
import AvatarWithTextItem from 'components/AvatarWithTextItem';
import AvatarWithTextItems from 'components/AvatarWithTextItems';
import EventTracking from 'lib/EventTracking';
import Avatar from 'components/Avatar.jsx';
import Group from 'entities/Group';
import type {
  GetOrganizationMember_organizations_members_members_user_adminVisibleDeviceDetails,
  GetOrganizationMember_organizations_members_members_user_familySummary,
} from '../queries/__generated__/GetOrganizationMember.js';
import GroupMemberFamily from 'groups/components/GroupMemberFamily.jsx';
import UsersAvatarWithText from 'users/components/UsersAvatarWithText';
import Textarea from 'components/Textarea.jsx';
import StartChatButton from 'user/components/StartChatButton.jsx';

import './PeopleListUserProfile.scss';

type Props = {
  user: User,
  rostered: boolean,
  canCall: boolean,
  canChat: boolean,
  canAddChild: boolean,
  canAddParent: boolean,
  canChangeNote: boolean,
  subtext: string,
  devices: GetOrganizationMember_organizations_members_members_user_adminVisibleDeviceDetails[],
  familySummary: ?GetOrganizationMember_organizations_members_members_user_familySummary,
  groupsOwned: Group[],
  groupsJoined: Group[],
  note: string,
  onNoteChange: (SyntheticEvent<HTMLTextAreaElement>) => void,
  onNoteBlur: () => void,
  onBackClicked: ?() => void,
  onGroupClicked: (entityUuid: string) => void,
  onFamilyMemberClicked: (userId: number) => void,
  onClose: () => void,
};

const avatarWithTextProps = {
  subtle: true,
};

export default class PeopleListUserProfile extends React.Component<Props> {
  devicesList = (
    devices: GetOrganizationMember_organizations_members_members_user_adminVisibleDeviceDetails[]
  ) => {
    if (isEmpty(devices)) {
      return null;
    }

    return (
      <AvatarWithTextItems>
        {devices.map(item => {
          const icon = item.type === 'sms' ? 'phone' : 'email-stroke';
          const subtext = item.type === 'sms' ? 'Phone' : 'Email';

          return (
            <AvatarWithTextItem
              {...avatarWithTextProps}
              key={item.id}
              title={item.endpoint}
              avatar={<Avatar glyphName={icon} />}
              subtext={subtext}
              data-test="device-item"
            />
          );
        })}
      </AvatarWithTextItems>
    );
  };

  groupsList = (groups: Group[]) => {
    const { onGroupClicked } = this.props;

    if (isEmpty(groups)) {
      return null;
    }

    return (
      <AvatarWithTextItems>
        {groups.map(item => {
          const groupUuid = item.getUuid();

          return (
            <AvatarWithTextItem
              {...avatarWithTextProps}
              key={groupUuid}
              action={<Glyph name="ellipses" solo aria-label="TODO" />}
              avatarSize="small"
              group={item}
              hideCode
              onClick={() => groupUuid && onGroupClicked(groupUuid)}
            />
          );
        })}
      </AvatarWithTextItems>
    );
  };

  handleSectionVisibilityChange = (indicesOfExpandedSections: number[]) => {
    if (indicesOfExpandedSections.includes(0)) {
      EventTracking.track('user.device_details.expand', {
        viewed_user_id: this.props.user.id,
      });
    }
  };

  render() {
    const {
      familySummary,
      devices,
      groupsJoined,
      groupsOwned,
      user,
      rostered,
      canCall,
      canChat,
      canAddParent,
      canAddChild,
      subtext,
      note,
      onNoteChange,
      canChangeNote,
      onNoteBlur,
      onFamilyMemberClicked,
      onBackClicked,
      onClose,
    } = this.props;

    const familyMemberCount = familySummary
      ? familySummary.parents.length + familySummary.children.length
      : 0;

    return (
      <PeopleListProfile
        onBackClicked={onBackClicked}
        onChange={this.handleSectionVisibilityChange}
        onClose={onClose}
        actions={
          <Buttons>
            <StartChatButton
              size="small"
              disabled={!canChat}
              subscriber={Subscriber.fromJSON(user.toJSON())}
            />
            {canCall && (
              <PhoneCallButton
                calleeName={user.getName()}
                calleeUuid={user.getUuid()}
                size="small"
              />
            )}
            <Button level="secondary" aria-label="TODO More options">
              <Glyph name="ellipses" solo />
            </Button>
          </Buttons>
        }
        header={
          <UsersAvatarWithText
            avatarSize="xxlarge"
            icon={
              <TooltipTrigger
                placement="top"
                tooltip={<Tooltip>{rostered ? 'SIS-linked' : 'Not SIS-linked'}</Tooltip>}>
                <span>
                  <Icon color={rostered ? 'shamrock' : 'koala'} name="link-linked" size="xlarge" />
                </span>
              </TooltipTrigger>
            }
            level="primary"
            subtext={subtext}
            user={user}
          />
        }
        sections={[
          {
            title: 'Contact information',
            children: this.devicesList(devices),
            count: devices.length,
          },
          {
            title: 'Family members',
            children: (
              <GroupMemberFamily
                canAddChild={canAddChild}
                canAddParent={canAddParent}
                familySummary={familySummary}
                orientation="vertical"
                userId={user.id}
                onFamilyMemberClick={onFamilyMemberClicked}
              />
            ),
            count: familyMemberCount,
          },
          {
            title: 'Classes owned',
            children: this.groupsList(groupsOwned),
            count: groupsOwned.length,
          },
          {
            title: 'Classes joined',
            children: this.groupsList(groupsJoined),
            count: groupsJoined.length,
          },
        ]}
        className="PeopleListUserProfile"
        noteField={
          <Textarea
            rows={2}
            value={note}
            onChange={onNoteChange}
            onBlur={onNoteBlur}
            disabled={!canChangeNote}
            placeholder="Add a note…"
          />
        }
      />
    );
  }
}
